import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import useThemeStyles from '../../../hooks/useThemeStyles';

const FileUploadStep = ({
  file,
  dragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileChange,
  onRemoveFile,
}) => {
  const { colors } = useThemeStyles();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" sx={{ color: colors.text.secondary, textAlign: 'center' }}>
        Upload a Microsoft Word document (.docx or .doc) to import into your project.
      </Typography>

      <Box
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        sx={{
          border: `2px dashed ${dragOver ? colors.primary.main : colors.border.subtle}`,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backgroundColor: dragOver ? `${colors.primary.main}10` : 'transparent',
          '&:hover': {
            borderColor: colors.primary.main,
            backgroundColor: `${colors.primary.main}05`,
          },
        }}
        component="label"
      >
        <input
          type="file"
          hidden
          accept=".docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
          onChange={onFileChange}
        />
        <CloudUploadIcon
          sx={{
            fontSize: 48,
            color: dragOver ? colors.primary.main : colors.text.secondary,
            mb: 2,
          }}
        />
        <Typography variant="h6" sx={{ mb: 1, color: colors.text.primary }}>
          {dragOver ? 'Drop file here' : 'Drag & drop your file here'}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
          or click to browse
        </Typography>
      </Box>

      {file && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            backgroundColor: colors.paper.backgroundSecondary,
            borderRadius: 1,
          }}
        >
          <DescriptionIcon sx={{ color: colors.primary.main }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ color: colors.text.primary }}>
              {file.name}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
          <Button size="small" color="error" onClick={onRemoveFile}>
            Remove
          </Button>
        </Box>
      )}
    </Box>
  );
};

FileUploadStep.propTypes = {
  file: PropTypes.shape({ name: PropTypes.string, size: PropTypes.number }),
  dragOver: PropTypes.bool.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onFileChange: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
};

FileUploadStep.defaultProps = {
  file: null,
};

export default FileUploadStep;
